import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { ArrowLeftRight, TriangleAlert, X } from "lucide-react";
import { type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { LearningObjectChoiceList } from "./LearningObjectChoiceList.component";
import { useIsAlternativeContent } from "../../context/LearningObjectAlternativeContext";
import { useSelectLearningObjectAlternativeMutation } from "../../queries";
import type { StructuredAlternativeSectionItem } from "../../utils/learningObjectStructure";
import { ButtonDigited } from "../Button/Button.variants";
import { CommonModal } from "../Commons/Modal.component";
import { StatusBox } from "../Commons/StatusBox.component";
import { CloseModalButton } from "../LearningActiviyModal/LearningActivity.styles";

type LearningObjectAlternativeSectionProps = {
  disabled?: boolean;
  index?: number;
  parentTypology?: "COURSE" | "PATH";
  section: StructuredAlternativeSectionItem;
  showChoice?: boolean;
};

export function LearningObjectAlternativeSection({
  disabled = false,
  index,
  parentTypology = "COURSE",
  section,
  showChoice = false
}: LearningObjectAlternativeSectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAlternativeContent = useIsAlternativeContent();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const {
    error,
    isPending,
    mutateAsync,
    reset
  } = useSelectLearningObjectAlternativeMutation();

  const openModal = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    reset();
    setSelectedId(section.selectedItem?.id ?? null);
    setOpen(true);
  };

  const closeModal = () => {
    if (isPending) return;
    setOpen(false);
    setSelectedId(null);
  };

  const hasNewChoice = selectedId != null
    && selectedId !== section.selectedItem?.id
    && section.alternatives.some((item) => item.id === selectedId);

  const handleSave = async () => {
    if (!hasNewChoice || selectedId == null || isPending || disabled) return;
    try {
      await mutateAsync({
        sectionId: section.id,
        selectedLearningObjectId: selectedId
      });
      setOpen(false);
      setSelectedId(null);
    } catch {
      // L'errore della mutation resta visibile nella modale e consente un nuovo tentativo.
    }
  };

  return (
    <>
      { section.selectedItem && !showChoice ? (
        <Button
          color="primary"
          disabled={ disabled || section.alternatives.length === 0 }
          onClick={ openModal }
          startIcon={ <ArrowLeftRight aria-hidden="true" size="1rem" /> }
          sx={ { textTransform: "none" } }
          variant="text"
        >
          { section.selectedItem.learningObjectTypology === "COURSE"
            ? t("alternative_section_change_course", { defaultValue: "Cambia corso" })
            : t("alternative_section_change_activity", { defaultValue: "Cambia attività" }) }
        </Button>
      ) : (
        <Box
          aria-label={ section.title || t("alternative_section_title", { defaultValue: "Contenuto alternativo" }) }
          component="section"
          data-testid="alternative-choice-section"
          sx={ {
            backgroundColor: theme.customColors.backgroundCard,
            border: `0.0625rem dashed ${theme.customColors.border}`,
            borderRadius: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            justifyContent: "space-between",
            minHeight: "9rem",
            padding: { sm: "1.25rem", xs: "1rem" }
          } }
        >
          <Stack alignItems="flex-start" direction="row" gap="1rem" justifyContent="space-between">
            <Stack alignItems="center" direction="row" gap="1.25rem" minWidth={ 0 }>
              { index != null && (
                <Typography color={ theme.customColors.textPrimary } fontSize="1.25rem" fontWeight={ 700 }>
                  { index }
                </Typography>
              ) }
              <Stack minWidth={ 0 }>
                <Typography
                  color={ theme.customColors.textPrimary }
                  fontSize="1.125rem"
                  fontWeight={ 500 }
                  sx={ { overflowWrap: "anywhere" } }
                >
                  { section.title || t("alternative_section_title", { defaultValue: "Contenuto alternativo" }) }
                </Typography>
                <Typography color={ theme.customColors.textPrimary } fontSize="0.875rem">
                  { t("alternative_section_to_choose", { defaultValue: "Da scegliere" }) }
                </Typography>
              </Stack>
            </Stack>
            { !isAlternativeContent && <Tooltip title={ t("mandatory", { defaultValue: "Obbligatorio" }) }>
              <Box sx={ { flexShrink: 0 } }>
                <StatusBox
                  ariaLabel={ t("mandatory", { defaultValue: "Obbligatorio" }) }
                  backgroundColor={ theme.customColors.backgroundWarning }
                  border={ `1px solid ${theme.customColors.borderWarning}` }
                  color={ theme.customColors.textTag }
                  icon={ <TriangleAlert aria-hidden="true" size="1rem" /> }
                  id={ `alternative-section-${section.id}` }
                  label=""
                  sxBox={ { height: "2rem", width: "2.125rem" } }
                />
              </Box>
            </Tooltip> }
          </Stack>
          <ButtonDigited
            buttonSize="sm"
            disabled={ disabled || section.alternatives.length === 0 }
            onClick={ openModal }
            sx={ { alignSelf: "flex-end", height: "32px" } }
            text={ parentTypology === "PATH"
              ? t("alternative_section_choose_object", { defaultValue: "Scegli oggetto" })
              : t("alternative_section_choose", { defaultValue: "Scegli attività" }) }
            typology="primary"
          />
        </Box>
      ) }

      { open && (
        <CommonModal
          avoidMobileSetting
          handleClose={ closeModal }
          open={ open }
          sxModalContent={ {
            borderRadius: "1rem",
            maxHeight: "calc(100dvh - 2rem)",
            maxWidth: "calc(100% - 2rem)",
            overflowY: "auto",
            padding: "1rem",
            width: "48rem",
            [theme.breakpoints.up("sm")]: { padding: "1.5rem" }
          } }
        >
          <Stack
            gap="1.5rem"
            onClick={ (event) => event.stopPropagation() }
          >
            <Stack alignItems="flex-start" direction="row" gap="1rem" justifyContent="space-between">
              <Stack gap="0.25rem" minWidth={ 0 }>
                <Typography
                  color={ theme.customColors.textPrimary }
                  fontSize="1.5rem"
                  fontWeight={ 700 }
                  id="modal-modal-title"
                >
                  { t("alternative_section_modal_title", { defaultValue: "Scegli l’oggetto formativo" }) }
                </Typography>
                <Typography
                  color={ theme.customColors.textPrimary }
                  fontSize="0.875rem"
                  id="modal-modal-description"
                >
                  { t("alternative_section_modal_hint", {
                    defaultValue: "Puoi anche modificare la tua selezione in seguito"
                  }) }
                </Typography>
              </Stack>
              <CloseModalButton
                aria-label={ t("close") }
                disabled={ isPending }
                onClick={ closeModal }
                type="button"
              >
                <X aria-hidden="true" size="1.5rem" />
              </CloseModalButton>
            </Stack>

            <LearningObjectChoiceList
              alternatives={ section.alternatives }
              disabled={ isPending }
              onSelect={ setSelectedId }
              selectedId={ selectedId }
            />

            { error && (
              <Typography color="error" fontSize="0.875rem" role="alert">
                { t("alternative_section_save_error", {
                  defaultValue: "Non è stato possibile salvare la scelta. Riprova."
                }) }
              </Typography>
            ) }

            <Stack direction="row" gap="0.75rem" justifyContent="flex-end">
              <ButtonDigited
                buttonSize="md"
                disabled={ !hasNewChoice || isPending || disabled }
                onClick={ handleSave }
                text={ isPending
                  ? <CircularProgress color="inherit" size="1rem" />
                  : t("save") }
                typology="primary"
              />
            </Stack>
          </Stack>
        </CommonModal>
      ) }
    </>
  );
}
